import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Redirect } from 'react-router';
import axios from '../../helpers/axios';
import { isUatEnvironment } from '../../helpers/uatMasking';
import { Grid, Card, CardContent, CardHeader, Typography, TextField, Button, Checkbox, IconButton, Tooltip, Box, CardActions, Collapse, FormControl, FormControlLabel, InputLabel, Select, MenuItem, ListItemText, Autocomplete, Modal, Backdrop, CircularProgress, Chip } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, NotesOutlined, CloseOutlined, PhotoCameraOutlined } from '@mui/icons-material';
import { withTranslation, WithTranslation } from 'react-i18next';
import OperationsHeader from './OperationsHeader';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 420,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
};

const mapStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '65%',
    p: 4,
};

type PickupDayCategory = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday' | 'twoDays' | 'unassigned';
type MapFilterCategory = Exclude<PickupDayCategory, 'twoDays'>;

interface MarkerProps {
    lat: number;
    lng: number;
    communityId?: number | string;
    label?: string;
    pickupDays: string[];
    category: PickupDayCategory;
}

const PICKUP_DAY_COLORS: Record<PickupDayCategory, string> = {
    Monday: '#2563eb',
    Tuesday: '#16a34a',
    Wednesday: '#d97706',
    Thursday: '#7c3aed',
    Friday: '#db2777',
    Saturday: '#0891b2',
    Sunday: '#dc2626',
    twoDays: '#0f172a',
    unassigned: '#64748b',
};

const PICKUP_DAY_LABELS: Record<PickupDayCategory, string> = {
    Monday: 'Monday',
    Tuesday: 'Tuesday',
    Wednesday: 'Wednesday',
    Thursday: 'Thursday',
    Friday: 'Friday',
    Saturday: 'Saturday',
    Sunday: 'Sunday',
    twoDays: 'Multiple Pickups',
    unassigned: 'No Pickup Day Assigned',
};

const DEFAULT_MAP_DAY_FILTERS: Record<MapFilterCategory, boolean> = {
    Monday: true,
    Tuesday: true,
    Wednesday: true,
    Thursday: true,
    Friday: true,
    Saturday: true,
    Sunday: true,
    unassigned: true,
};

export interface IProps {
    user: {
        id: number;
        name: string;
        email: string;
        role: string;
        is_active: boolean;
        selectedHierarchies: any[];
        selectedHierarchyIds: any[];
    };
}

interface IState {
    expandedCommunityId: number | null;
    selectedPickupDays: string[];
    draftPickupDays: string[];
    pickupDayMenuOpen: boolean;
    selectedCommunities: any[];
    communities: any[];
    communityUsersPickupPoints: any;
    loadingCommunityUsers: any;
    showNotesModal: boolean;
    selectedNotes: Array<{
        sender: string;
        timestamp: string;
        message: string;
    }>;
    showImagesModal: boolean;
    selectedImages: string[];
    showLoader: boolean;
    showMapView: boolean;
    markers: MarkerProps[];
    mapDayFilters: Record<MapFilterCategory, boolean>;
    defaultMapCenter: { lat: number; lng: number };
}

class PickupPoints extends React.Component<RouteComponentProps<any> & IProps & WithTranslation, IState> {
    constructor(props: RouteComponentProps<any> & IProps & WithTranslation) {
        super(props);
        this.state = {
            expandedCommunityId: null,
            selectedPickupDays: [],
            draftPickupDays: [],
            pickupDayMenuOpen: false,
            selectedCommunities: [],
            communities: [],
            communityUsersPickupPoints: {},
            loadingCommunityUsers: {},
            showNotesModal: false,
            selectedNotes: [],
            showImagesModal: false,
            selectedImages: [],
            showLoader: false,
            showMapView: false,
            markers: [],
            mapDayFilters: { ...DEFAULT_MAP_DAY_FILTERS },
            defaultMapCenter: { lat: 22.5937, lng: 78.9629 },
        };
    }

    private pickupDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    private mapDayCategories: MapFilterCategory[] = [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
        'unassigned',
    ];

    private getInitialMapDayFilters = (): Record<MapFilterCategory, boolean> => {
        if (this.state.selectedPickupDays.length === 0) {
            return { ...DEFAULT_MAP_DAY_FILTERS };
        }

        return this.mapDayCategories.reduce((filters, category) => {
            filters[category] = this.state.selectedPickupDays.includes(category);
            return filters;
        }, {} as Record<MapFilterCategory, boolean>);
    };

    private parsePickupDays = (value: any): string[] => {
        const values = Array.isArray(value)
            ? value
            : typeof value === 'string'
                ? value.split(',')
                : [];

        return values.reduce((days: string[], value: any) => {
            const normalizedDay = this.pickupDays.find(
                (day) => day.toLowerCase() === String(value).trim().toLowerCase()
            );

            if (normalizedDay && !days.includes(normalizedDay)) {
                days.push(normalizedDay);
            }

            return days;
        }, []);
    };

    private getPickupDayCategory = (pickupDays: string[]): PickupDayCategory => {
        if (pickupDays.length === 0) {
            return 'unassigned';
        }

        if (pickupDays.length > 1) {
            return 'twoDays';
        }

        return pickupDays[0] as PickupDayCategory;
    };

    private getMarkerIcon = (category: PickupDayCategory) => {
        const color = PICKUP_DAY_COLORS[category].replace('#', '%23');

        return {
            url: `data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='34' viewBox='0 0 20 34'%3E%3Cpath fill='${color}' stroke='%23FFF' stroke-width='1.5' d='M10 0C4.48 0 0 4.48 0 10c0 1.4.28 2.73.79 3.95L10 34l9.21-20.05c.51-1.22.79-2.55.79-3.95C20 4.48 15.52 0 10 0z'/%3E%3Ccircle fill='%23FFF' cx='10' cy='10' r='3.5'/%3E%3C/svg%3E`,
            scaledSize: window.google?.maps ? new window.google.maps.Size(26, 42) : undefined,
            anchor: window.google?.maps ? new window.google.maps.Point(13, 42) : undefined,
        };
    };

    private renderStatusCheckbox = (checked: boolean) => (
        <Checkbox
            checked={checked}
            disableRipple
            sx={{
                p: 0,
                pointerEvents: 'none',
                color: 'rgba(0, 0, 0, 0.54)',
                '&.Mui-checked': {
                    color: '#2e7d32',
                },
                '& .MuiSvgIcon-root': {
                    fontSize: 20,
                },
            }}
        />
    );

    private renderMaskedValue = (value: React.ReactNode) => (
        <Box
            component="span"
            sx={isUatEnvironment ? {
                filter: 'blur(4px)',
                opacity: 0.5,
                userSelect: 'none',
            } : undefined}
        >
            {value}
        </Box>
    );

    public componentDidMount(): void {
        this.fetchCommunities();
    }

    private fetchCommunities = () => {
        this.setState({ showLoader: true });

        const body = {
            hierarchies: this.props.user.selectedHierarchyIds,
        };

        axios.post(`allCommunitiesPickupStatus`, body, {
            headers: { "Content-Type": "application/json" },
        }).then((response) => {
            const communities = response.data.data || [];
            this.setState({ communities }, () => this.fetchMapPoints(communities));
        }).catch(() => {
            this.setState({ showLoader: false });
        });
    }

    private fetchMapPoints = (communities: any[]) => {
        if (communities.length === 0) {
            this.setState({ markers: [], showLoader: false });
            return;
        }

        axios.post(`getCommunitiesPickupMapPoints`, {
            community_ids: communities.map((community: any) => community.id),
            pickup_days: [],
        }, {
            headers: { "Content-Type": "application/json" },
        }).then((response) => {
            const rows = response.data.data || [];
            let defaultMapCenter = this.state.defaultMapCenter;
            const markers: MarkerProps[] = [];

            rows.forEach((row: any) => {
                const lat = parseFloat(row.latitude);
                const lng = parseFloat(row.longitude);

                if (!isNaN(lat) && !isNaN(lng)) {
                    const community = communities.find((item: any) =>
                        String(item.id) === String(row.community_id ?? row.id)
                    );
                    const pickupDays = this.parsePickupDays(
                        row.pickup_ref_days ?? row.pickup_days ?? row.pickup_day ?? community?.pickup_ref_days
                    );

                    if (markers.length === 0) {
                        defaultMapCenter = { lat, lng };
                    }

                    markers.push({
                        lat,
                        lng,
                        communityId: row.community_id ?? community?.id,
                        label: row.community_name || community?.name || row.name,
                        pickupDays,
                        category: this.getPickupDayCategory(pickupDays),
                    });
                }
            });

            this.setState({
                markers,
                defaultMapCenter,
                showLoader: false,
            });
        }).catch(() => {
            this.setState({ showLoader: false });
        });
    };

    private fetchCommunityUsersPickupPoints = (communityId: number) => {
        if (this.state.communityUsersPickupPoints[communityId] || this.state.loadingCommunityUsers[communityId]) {
            return;
        }

        this.setState((prevState) => ({
            showLoader: true,
            loadingCommunityUsers: {
                ...prevState.loadingCommunityUsers,
                [communityId]: true,
            },
        }));

        axios.post(`getCommunityUsersPickupPoints/${communityId}`, {}, {
            headers: { "Content-Type": "application/json" },
        }).then((response) => {
            this.setState((prevState) => ({
                communityUsersPickupPoints: {
                    ...prevState.communityUsersPickupPoints,
                    [communityId]: response.data.data || [],
                },
                loadingCommunityUsers: {
                    ...prevState.loadingCommunityUsers,
                    [communityId]: false,
                },
                showLoader: false,
            }));
        }).catch(() => {
            this.setState((prevState) => ({
                loadingCommunityUsers: {
                    ...prevState.loadingCommunityUsers,
                    [communityId]: false,
                },
                showLoader: false,
            }));
        });
    }

    private getFilteredCommunities = () => {
        return this.state.communities.filter((community: any) => {
            const communityMatches = this.state.selectedCommunities.length === 0 ||
                this.state.selectedCommunities.some((selectedCommunity: any) => community.id === selectedCommunity.id);
            const pickupDays = (community.pickup_ref_days || '').split(',').map((day: string) => day.trim());
            const pickupDayMatches = this.state.selectedPickupDays.length === 0 ||
                this.state.selectedPickupDays.some((selectedDay) => pickupDays.includes(selectedDay));

            return communityMatches && pickupDayMatches;
        });
    }

    private getVisibleMapMarkers = (mapDayFilters = this.state.mapDayFilters) => {
        return this.state.markers.filter((marker) => {
            const communityMatches = this.state.selectedCommunities.length === 0 ||
                this.state.selectedCommunities.some((community: any) =>
                    String(community.id) === String(marker.communityId)
                );

            if (!communityMatches) {
                return false;
            }

            if (marker.category === 'twoDays') {
                return marker.pickupDays.every(
                    (day) => mapDayFilters[day as MapFilterCategory]
                );
            }

            return mapDayFilters[marker.category];
        });
    };

    private viewOnMap = () => {
        const mapDayFilters = this.getInitialMapDayFilters();
        const visibleMarkers = this.getVisibleMapMarkers(mapDayFilters);

        this.setState({
            mapDayFilters,
            defaultMapCenter: visibleMarkers.length
                ? { lat: visibleMarkers[0].lat, lng: visibleMarkers[0].lng }
                : this.state.defaultMapCenter,
            showMapView: true,
        });
    };

    private toggleCommunity = (communityId: number) => {
        this.setState((prevState) => {
            const expandedCommunityId = prevState.expandedCommunityId === communityId ? null : communityId;

            return { expandedCommunityId };
        }, () => {
            if (this.state.expandedCommunityId) {
                this.fetchCommunityUsersPickupPoints(this.state.expandedCommunityId);
            }
        });
    }

    private openPickupDayMenu = () => {
        this.setState({
            pickupDayMenuOpen: true,
            draftPickupDays: this.state.selectedPickupDays,
        });
    }

    private closePickupDayMenu = () => {
        this.setState({
            pickupDayMenuOpen: false,
            draftPickupDays: this.state.selectedPickupDays,
        });
    }

    private applyPickupDayFilter = () => {
        this.setState({
            selectedPickupDays: this.state.draftPickupDays,
            pickupDayMenuOpen: false,
        });
    }

    private openNotesModal = (row: any) => {
        const notes = row.notes || '';
        const notesArray = notes ? [{
            sender: row.name || 'Unknown',
            timestamp: row.created_at || new Date().toISOString(),
            message: notes
        }] : [];
        this.setState({
            showNotesModal: true,
            selectedNotes: notesArray,
        });
    }

    private closeNotesModal = () => {
        this.setState({
            showNotesModal: false,
            selectedNotes: [],
        });
    }

    private parsePickupPointImages = (images: any): string[] => {
        if (!images) {
            return [];
        }

        if (Array.isArray(images)) {
            return images.filter((image) => typeof image === 'string' && image.trim() !== '');
        }

        if (typeof images === 'string') {
            try {
                const parsedImages = JSON.parse(images);
                if (Array.isArray(parsedImages)) {
                    return parsedImages.filter((image) => typeof image === 'string' && image.trim() !== '');
                }
            } catch (error) {
                return images.split(',').map((image) => image.trim()).filter(Boolean);
            }
        }

        return [];
    }

    private openImagesModal = (row: any) => {
        this.setState({
            showImagesModal: true,
            selectedImages: this.parsePickupPointImages(row.images),
        });
    }

    private closeImagesModal = () => {
        this.setState({
            showImagesModal: false,
            selectedImages: [],
        });
    }

    public render() {
        if (!localStorage.getItem('token')) {
            return (
                <Redirect to="/login" />
            );
        }
        const { t } = this.props;

        const filteredCommunities = this.getFilteredCommunities();
        const filteredMapMarkers = this.getVisibleMapMarkers();

        return (
            <div>
                <Backdrop
                    sx={{ zIndex: (theme) => theme.zIndex.drawer + 1000 }}
                    open={this.state.showLoader}
                >
                    <CircularProgress className='customLoader' />
                </Backdrop>
                <OperationsHeader />
                <Grid container spacing={2}>
                    <Grid item md={12} xs={12} className="mt-3">
                        <Card style={{ height: 600 }}>
                            <CardHeader
                                title={
                                    <Grid container alignItems="center" spacing={2}>
                                        <Grid item md={2.5} xs={12}>
                                            <Typography variant='h5' className='MuiCardHeader-title' sx={{ lineHeight: "40px", whiteSpace: 'nowrap' }}>
                                                {t("Pickup Points")}
                                            </Typography>
                                        </Grid>
                                        <Grid item md={9.5} xs={12}>
                                            <Grid container spacing={2}>
                                                <Grid item md={3.5} xs={12}>
                                                    <Autocomplete
                                                        id="pickup-points-community"
                                                        options={this.state.communities}
                                                        size="small"
                                                        fullWidth
                                                        multiple
                                                        isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                                                        getOptionLabel={(option: any) => option.name || ''}
                                                        value={this.state.selectedCommunities}
                                                        onChange={(event: any, value: any) => this.setState({ selectedCommunities: value })}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={t("Community")}
                                                                placeholder={t("Select Community")}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item md={3.5} xs={12}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel id="pickup-day-filter-label">{t("Search by Pickup Day")}</InputLabel>
                                                        <Select
                                                            labelId="pickup-day-filter-label"
                                                            multiple
                                                            value={this.state.pickupDayMenuOpen ? this.state.draftPickupDays : this.state.selectedPickupDays}
                                                            label={t("Search by Pickup Day")}
                                                            renderValue={(selected: any) => selected.join(', ')}
                                                            open={this.state.pickupDayMenuOpen}
                                                            onOpen={this.openPickupDayMenu}
                                                            onClose={this.closePickupDayMenu}
                                                            onChange={(event: any) => this.setState({ draftPickupDays: event.target.value })}
                                                        >
                                                            {this.pickupDays.map((day) => (
                                                                <MenuItem key={day} value={day}>
                                                                    <Checkbox checked={this.state.draftPickupDays.indexOf(day) > -1} size="small" />
                                                                    <ListItemText primary={day} />
                                                                </MenuItem>
                                                            ))}
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    justifyContent: 'center',
                                                                    gap: 1.5,
                                                                    px: 2,
                                                                    py: 1.25,
                                                                    borderTop: '1px solid #eeeeee',
                                                                }}
                                                                onClick={(event) => event.stopPropagation()}
                                                            >
                                                                <Button size="medium" onClick={this.closePickupDayMenu}>
                                                                    {t("Close")}
                                                                </Button>
                                                                <Button size="medium" variant="contained" sx={{ outline: 'none !important' }} onClick={this.applyPickupDayFilter}>
                                                                    {t("Apply")}
                                                                </Button>
                                                            </Box>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                }
                                className="text-left"
                                action={
                                    <CardActions sx={{ pr: 2, pt: 1.5 }}>
                                        <Button variant="contained" size="small" sx={{ outline: 'none !important' }} onClick={this.viewOnMap}>
                                            {t("View on Map")}
                                        </Button>
                                    </CardActions>
                                }
                            />
                            <CardContent sx={{ maxHeight: 500, overflowY: 'auto' }}>
                                <Box
                                    role="table"
                                    aria-label="Pickup Points"
                                    sx={{
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box
                                        role="row"
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '64px 1.4fr 1fr 0.8fr 0.8fr 1.2fr 1fr 1.2fr',
                                            alignItems: 'center',
                                            minHeight: 48,
                                            px: 2,
                                            bgcolor: '#f5f5f5',
                                            borderBottom: '1px solid #e0e0e0',
                                            columnGap: 1,
                                        }}
                                    >
                                        <Typography variant="body2" fontWeight={600}></Typography>
                                        <Typography variant="body2" fontWeight={600}>{t("Community Name")}</Typography>
                                        <Typography variant="body2" fontWeight={600}>{t("Users")}</Typography>
                                        <Typography variant="body2" fontWeight={600}>{t("Lat")}</Typography>
                                        <Typography variant="body2" fontWeight={600}>{t("Long")}</Typography>
                                        <Typography variant="body2" fontWeight={600}>{t("Pickup Day")}</Typography>
                                        <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'center' }}>{t("Notes")}</Typography>
                                        <Typography variant="body2" fontWeight={600}>{t("Status")}</Typography>
                                    </Box>
                                    {filteredCommunities.map((community: any) => {
                                        const communityRows = this.state.communityUsersPickupPoints[community.id] || [];
                                        const isExpanded = this.state.expandedCommunityId === community.id;
                                        const pickupDays = community.pickup_ref_days || '';
                                        const allUserLevelTicksEnabled = community.status === 1 || community.status === '1' || community.status === true;

                                        return (
                                            <Box key={community.id}>
                                                <Box
                                                    role="row"
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '64px 1.4fr 1fr 0.8fr 0.8fr 1.2fr 1fr 1.2fr',
                                                        alignItems: 'center',
                                                        minHeight: 52,
                                                        px: 2,
                                                        borderBottom: isExpanded ? 'none' : '1px solid #eeeeee',
                                                        columnGap: 1,
                                                    }}
                                                >
                                                    <Box>
                                                        <Tooltip title={isExpanded ? t("Collapse") : t("Expand")}>
                                                            <IconButton size="small" onClick={() => this.toggleCommunity(community.id)}>
                                                                {isExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{community.name}</Typography>
                                                    <Typography variant="body2"></Typography>
                                                    <Typography variant="body2"></Typography>
                                                    <Typography variant="body2"></Typography>
                                                    <Typography variant="body2">{pickupDays}</Typography>
                                                    <Box sx={{ width: '100%' }}></Box>
                                                    {this.renderStatusCheckbox(allUserLevelTicksEnabled)}
                                                </Box>
                                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                    <Box
                                                        sx={{
                                                            px: 2,
                                                            py: 1,
                                                            bgcolor: '#fafafa',
                                                            borderBottom: '1px solid #eeeeee',
                                                        }}
                                                    >
                                                        {!this.state.loadingCommunityUsers[community.id] && communityRows.map((row: any) => (
                                                            <Box
                                                                key={row.user_id}
                                                                role="row"
                                                                sx={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: '64px 1.4fr 1fr 0.8fr 0.8fr 1.2fr 1fr 1.2fr',
                                                                    alignItems: 'center',
                                                                    minHeight: 44,
                                                                    columnGap: 1,
                                                                    borderTop: '1px solid #eeeeee',
                                                                }}
                                                            >
                                                                <Box></Box>
                                                                <Typography variant="body2"></Typography>
                                                                <Typography variant="body2">{this.renderMaskedValue(row.name)}</Typography>
                                                                <Typography variant="body2">{row.latitude !== null && row.latitude !== undefined && row.latitude !== '' ? row.latitude : '-'}</Typography>
                                                                <Typography variant="body2">{row.longitude !== null && row.longitude !== undefined && row.longitude !== '' ? row.longitude : '-'}</Typography>
                                                                <Typography variant="body2"></Typography>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, width: '100%' }}>
                                                                    <Tooltip title="Pickup point notes">
                                                                        <IconButton size="small" onClick={() => this.openNotesModal(row)}>
                                                                            <NotesOutlined fontSize="small" color="action" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Pickup point images">
                                                                        <IconButton size="small" onClick={() => this.openImagesModal(row)}>
                                                                            <PhotoCameraOutlined fontSize="small" color="action" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>
                                                                {this.renderStatusCheckbox(row.status === 1 || row.status === '1' || row.status === true)}
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Collapse>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Modal
                    open={this.state.showNotesModal}
                    onClose={this.closeNotesModal}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Card
                        sx={{
                            width: 500,
                            maxHeight: "80vh",
                            display: "flex",
                            flexDirection: "column",
                            borderRadius: 2,
                            boxShadow: 6,
                        }}
                    >
                        <Box
                            sx={{
                                p: 2,
                                borderBottom: "1px solid #e0e0e0",
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {t("Notes")}
                            </Typography>
                            <IconButton
                                onClick={this.closeNotesModal}
                                sx={{ position: "absolute", top: 8, right: 8 }}
                            >
                                <CloseOutlined />
                            </IconButton>
                        </Box>
                        <CardContent
                            sx={{
                                flexGrow: 1,
                                overflowY: "auto",
                                p: 2,
                                backgroundColor: "#f9fafb",
                            }}
                        >
                            {this.state.selectedNotes && this.state.selectedNotes.length > 0 ? (
                                <>
                                    {this.state.selectedNotes.map((note, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                mb: 2,
                                                p: 2,
                                                backgroundColor: "#ffffff",
                                                border: "1px solid #e2e8f0",
                                                borderRadius: 2,
                                                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                                            }}
                                        >
                                            {/* Header Row */}
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    mb: 1,
                                                }}
                                            >
                                                <Typography variant="caption" color="textSecondary">
                                                    {new Date(note.timestamp).toLocaleString("en-IN", {
                                                        day: "2-digit",
                                                        month: "short",
                                                        year: "numeric",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                        hour12: true,
                                                    })}
                                                </Typography>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{ fontWeight: 600, color: "#1e293b" }}
                                                >
                                                    {note.sender}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "#334155",
                                                    lineHeight: 1.6,
                                                    whiteSpace: "pre-wrap",
                                                }}
                                            >
                                                {note.message}
                                            </Typography>
                                        </Box>
                                    ))}
                                </>
                            ) : (
                                <Box sx={{ textAlign: "center", py: 4 }}>
                                    <Typography variant="body1" color="textSecondary">
                                        {t("No notes available")}
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                        <Box
                            sx={{
                                p: 2,
                                borderTop: "1px solid #e0e0e0",
                                display: "flex",
                                justifyContent: "flex-end",
                                backgroundColor: "#f8fafc",
                            }}
                        >
                            <Button
                                size="small"
                                variant="contained"
                                sx={{ outline: "none !important" }}
                                onClick={this.closeNotesModal}
                            >
                                {t("Close")}
                            </Button>
                        </Box>
                    </Card>
                </Modal>
                <Modal
                    open={this.state.showImagesModal}
                    onClose={this.closeImagesModal}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Card
                        sx={{
                            width: { xs: "92vw", sm: 720 },
                            maxHeight: "86vh",
                            display: "flex",
                            flexDirection: "column",
                            borderRadius: 2,
                            boxShadow: 6,
                        }}
                    >
                        <Box
                            sx={{
                                p: 2,
                                borderBottom: "1px solid #e0e0e0",
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {t("Pickup Point Images")}
                            </Typography>
                            <IconButton
                                onClick={this.closeImagesModal}
                                sx={{ position: "absolute", top: 8, right: 8 }}
                            >
                                <CloseOutlined />
                            </IconButton>
                        </Box>
                        <CardContent
                            sx={{
                                flexGrow: 1,
                                overflowY: "auto",
                                p: 2,
                                backgroundColor: "#f9fafb",
                            }}
                        >
                            {this.state.selectedImages.length > 0 ? (
                                <Grid container spacing={2}>
                                    {this.state.selectedImages.map((image, index) => (
                                        <Grid item xs={12} sm={6} key={`${image}-${index}`}>
                                            <Box
                                                sx={{
                                                    border: "1px solid #e2e8f0",
                                                    borderRadius: 1,
                                                    backgroundColor: "#ffffff",
                                                    p: 1,
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={image}
                                                    alt={`Pickup point ${index + 1}`}
                                                    sx={{
                                                        display: "block",
                                                        width: "100%",
                                                        maxHeight: 320,
                                                        objectFit: "contain",
                                                        borderRadius: 1,
                                                        backgroundColor: "#f1f5f9",
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <Box sx={{ textAlign: "center", py: 4 }}>
                                    <Typography variant="body1" color="textSecondary">
                                        {t("No images")}
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                        <Box
                            sx={{
                                p: 2,
                                borderTop: "1px solid #e0e0e0",
                                display: "flex",
                                justifyContent: "flex-end",
                                backgroundColor: "#f8fafc",
                            }}
                        >
                            <Button
                                size="small"
                                variant="contained"
                                sx={{ outline: "none !important" }}
                                onClick={this.closeImagesModal}
                            >
                                {t("Close")}
                            </Button>
                        </Box>
                    </Card>
                </Modal>
                <Modal
                    open={this.state.showMapView}
                    onClose={() => this.setState({ showMapView: false })}
                    aria-labelledby="pickup-points-map-title"
                    aria-describedby="pickup-points-map-description"
                >
                    <Box sx={mapStyle}>
                        <Card>
                            <CardHeader
                                title={t("Pickup Points Map")}
                                className="text-left"
                                action={
                                    <IconButton color="primary" className='customIconButton' aria-label="close" component="span" onClick={() => this.setState({ showMapView: false })}>
                                        <CloseOutlined />
                                    </IconButton>
                                }
                                sx={{ paddingBottom: '0px' }}
                            />
                            <CardContent>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                        mb: 2,
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mr: 0.5 }}>
                                        {t("Filter by Pickup Day")}:
                                    </Typography>
                                    {this.mapDayCategories.map((category) => (
                                        <FormControlLabel
                                            key={category}
                                            sx={{ m: 0 }}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={this.state.mapDayFilters[category]}
                                                    onChange={() => this.setState((prevState) => ({
                                                        mapDayFilters: {
                                                            ...prevState.mapDayFilters,
                                                            [category]: !prevState.mapDayFilters[category],
                                                        },
                                                    }))}
                                                    sx={{
                                                        color: PICKUP_DAY_COLORS[category],
                                                        '&.Mui-checked': { color: PICKUP_DAY_COLORS[category] },
                                                    }}
                                                />
                                            }
                                            label={
                                                <Chip
                                                    size="small"
                                                    label={t(PICKUP_DAY_LABELS[category])}
                                                    sx={{
                                                        bgcolor: PICKUP_DAY_COLORS[category],
                                                        color: '#ffffff',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            }
                                        />
                                    ))}
                                    <Chip
                                        size="small"
                                        label={t(PICKUP_DAY_LABELS.twoDays)}
                                        sx={{
                                            bgcolor: PICKUP_DAY_COLORS.twoDays,
                                            color: '#ffffff',
                                            fontWeight: 600,
                                        }}
                                    />
                                </Box>
                                <div className="map-container">
                                    <LoadScript googleMapsApiKey={'AIzaSyCFqWukKnjwW2mFD8Yied3129Dic2TRWH4'}>
                                        <GoogleMap
                                            mapContainerStyle={{ height: '72vh', width: '100%' }}
                                            zoom={filteredMapMarkers.length ? 12 : 5.3}
                                            center={this.state.defaultMapCenter}
                                        >
                                            {filteredMapMarkers.map((marker, index) => (
                                                <Marker
                                                    key={index}
                                                    position={{ lat: marker.lat, lng: marker.lng }}
                                                    icon={this.getMarkerIcon(marker.category)}
                                                    title={`${marker.label || t("Community")}\n${marker.pickupDays.length
                                                        ? marker.pickupDays.join(', ')
                                                        : t(PICKUP_DAY_LABELS.unassigned)}`}
                                                />
                                            ))}
                                        </GoogleMap>
                                    </LoadScript>
                                </div>
                            </CardContent>
                        </Card>
                    </Box>
                </Modal>
            </div>
        )
    }
}

export default withRouter(withTranslation()(PickupPoints))
